import React from "react";
import { Box } from "@mui/material";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";
import ThumbUpAltRoundedIcon from "@mui/icons-material/ThumbUpAltRounded";
import ThumbDownAltRoundedIcon from "@mui/icons-material/ThumbDownAltRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import { resizedImage } from "../../../utils/resizedImage";
import { BUTTON_RESET, C, FOCUS_RING, FONT_BODY, FONT_DISPLAY, SHADOW } from "../../../utils/designTokens";
import { titleCaseIfShouting } from "../orderText";
import { itemLineTotal, paidWith, receiptLines, refundedQuantities } from "./orderProgress";

const money = (v) => `${v < 0 ? "−" : ""}$${Math.abs(v).toFixed(2)}`;
const GREY_LINE = { display: "block", fontFamily: FONT_BODY, fontSize: 13, color: C.muted, lineHeight: 1.45 };

// Options picked under a modifier, to any depth, one indent per level.
function OptionLines({ groups, depth = 0 }) {
  return (groups || []).map((g, gi) => (
    <React.Fragment key={gi}>
      {g.title && (g.options || []).length > 0 && (
        <Box component="span" sx={{ ...GREY_LINE, pl: depth * 1.5, fontStyle: "italic" }}>
          {titleCaseIfShouting(g.title)}
          {parseFloat(g.additionalCost) > 0 ? ` (+${money(parseFloat(g.additionalCost))})` : ""}
        </Box>
      )}
      {(g.options || []).map((opt, oi) => {
        const q = parseInt(opt.qty ?? opt.quantity, 10) || 1;
        const p = parseFloat(opt.price) || 0;
        return (
          <React.Fragment key={oi}>
            <Box component="span" sx={{ ...GREY_LINE, pl: depth * 1.5 + 1 }}>
              {titleCaseIfShouting(opt.title)}
              {q > 1 ? ` ×${q}` : ""}
              {p > 0 ? ` (+${money(p * q)})` : ""}
            </Box>
            {(opt.addons || []).length > 0 && <OptionLines groups={opt.addons} depth={depth + 1} />}
          </React.Fragment>
        );
      })}
    </React.Fragment>
  ));
}

// The same option picked twice was stored twice on older web orders; show it once with its count.
function dedupe(addons) {
  return (addons || []).map((a) => {
    const byId = {};
    (a.options || []).forEach((opt) => {
      const key = opt._id || opt.title;
      const q = parseInt(opt.quantity, 10) || 1;
      if (byId[key]) byId[key].qty += q;
      else byId[key] = { ...opt, qty: q };
    });
    return { ...a, options: Object.values(byId) };
  });
}

function Vote({ dir, pressed, label, onClick }) {
  const up = dir > 0;
  const Icon = pressed ? (up ? ThumbUpAltRoundedIcon : ThumbDownAltRoundedIcon) : up ? ThumbUpAltOutlinedIcon : ThumbDownAltOutlinedIcon;
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-pressed={pressed}
      aria-label={label}
      sx={{
        ...BUTTON_RESET,
        ...FOCUS_RING,
        boxSizing: "border-box",
        width: 36,
        height: 36,
        borderRadius: "10px",
        border: `1.5px solid ${pressed ? (up ? C.green : C.red) : C.borderStrong}`,
        bgcolor: pressed ? (up ? C.greenSoft : C.redTint) : C.bg,
        color: pressed ? (up ? C.green : C.red) : C.muted,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "background-color .15s ease, border-color .15s ease",
        "&:hover": { borderColor: up ? C.green : C.red },
      }}
    >
      <Icon sx={{ fontSize: 18 }} />
    </Box>
  );
}

/**
 * The receipt: items (with their options and refunds), "How was it?" once
 * delivered, the breakdown and how it was paid. The breakdown comes from
 * receiptLines, so the total is always the sum of the lines.
 */
function Receipt({ order: o, canRateDishes, myVotes = {}, onVoteDish, sx }) {
  const { lines, total } = receiptLines(o);
  const paid = paidWith(o);
  const refunded = refundedQuantities(o);
  const logo = o.restaurant?.logo || o.restaurant?.image;

  return (
    <Box
      component="section"
      aria-labelledby="od-receipt-title"
      sx={{ boxSizing: "border-box", bgcolor: C.bg, border: `1px solid ${C.border}`, borderRadius: "22px", boxShadow: SHADOW.float, p: { xs: "20px", sm: "22px 24px" }, ...sx }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box sx={{ width: 44, height: 44, flexShrink: 0, borderRadius: "50%", overflow: "hidden", bgcolor: C.surface2, display: "flex", alignItems: "center", justifyContent: "center", color: C.muted }}>
          {logo ? <Box component="img" src={resizedImage(logo, 128)} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <StorefrontRoundedIcon aria-hidden="true" />}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Box component="h2" id="od-receipt-title" sx={{ m: 0, fontFamily: FONT_DISPLAY, fontSize: 20, fontWeight: 700, letterSpacing: "-0.01em", color: C.ink }}>
            Receipt
          </Box>
          <Box sx={{ fontFamily: FONT_BODY, fontSize: 14, color: C.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{o.restaurant?.name}</Box>
        </Box>
      </Box>

      <Box component="ul" aria-label="Items" sx={{ m: 0, mt: 2, p: 0, listStyle: "none", borderTop: `1px solid ${C.border}` }}>
        {(o.items || []).map((item) => {
          const qty = parseInt(item.quantity, 10) || 1;
          const refundQty = refunded.get(String(item.title || "").toLowerCase()) || 0;
          const fullyRefunded = refundQty > 0 && refundQty >= qty;
          const size = String(item.variation?.title || "").trim();
          const showSize = size && !/^default$/i.test(size) && size.toLowerCase() !== String(item.title || "").trim().toLowerCase();
          const vote = myVotes[String(item.food)];
          const name = titleCaseIfShouting(item.title);
          const sizePrice = parseFloat(item.variation?.discounted) > 0 ? parseFloat(item.variation.discounted) : parseFloat(item.variation?.price || 0);
          return (
            <Box component="li" key={item._id} sx={{ py: 1.5, borderBottom: `1px solid ${C.border}` }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
                <Box component="span" sx={{ flexShrink: 0, minWidth: 30, height: 24, px: 0.75, boxSizing: "border-box", borderRadius: "8px", bgcolor: C.surface2, color: C.ink, fontFamily: FONT_BODY, fontSize: 13, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", fontVariantNumeric: "tabular-nums" }}>
                  {qty}×
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ fontFamily: FONT_BODY, fontSize: 15, fontWeight: 600, color: fullyRefunded ? C.muted : C.ink, lineHeight: 1.35, textDecoration: fullyRefunded ? "line-through" : "none" }}>
                    {name}
                  </Box>
                  {showSize && <Box component="span" sx={GREY_LINE}>{titleCaseIfShouting(size)}</Box>}
                  <OptionLines groups={dedupe(item.addons)} />
                  {item.specialInstructions && <Box component="span" sx={{ ...GREY_LINE, fontStyle: "italic" }}>Note: {item.specialInstructions}</Box>}
                  {refundQty > 0 && (
                    <Box component="span" sx={{ display: "inline-flex", mt: 0.5, px: 1, height: 22, alignItems: "center", borderRadius: "999px", bgcolor: C.greenSoft, color: C.green, fontFamily: FONT_BODY, fontSize: 12, fontWeight: 700 }}>
                      {fullyRefunded ? "Refunded" : `${refundQty} refunded`}
                    </Box>
                  )}
                </Box>
                <Box sx={{ flexShrink: 0, textAlign: "right", fontFamily: FONT_BODY, fontVariantNumeric: "tabular-nums" }}>
                  <Box sx={{ fontSize: 15, fontWeight: 600, color: fullyRefunded ? C.muted : C.ink, textDecoration: fullyRefunded ? "line-through" : "none" }}>{money(itemLineTotal(item))}</Box>
                  {refundQty > 0 && !fullyRefunded && <Box sx={{ fontSize: 13, fontWeight: 600, color: C.green }}>{money(-sizePrice * refundQty)}</Box>}
                </Box>
              </Box>
              {canRateDishes && item.food && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.25, pl: "42px" }}>
                  <Box component="span" sx={{ fontFamily: FONT_BODY, fontSize: 13, fontWeight: 600, color: C.muted, mr: 0.5 }}>
                    How was it?
                  </Box>
                  <Vote dir={1} pressed={vote === 1} label={`Liked ${name}`} onClick={() => onVoteDish?.(item.food, 1)} />
                  <Vote dir={-1} pressed={vote === -1} label={`Didn't like ${name}`} onClick={() => onVoteDish?.(item.food, -1)} />
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      <Box component="dl" sx={{ m: 0, mt: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
        {lines.map((l) => (
          <Box key={l.key} sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 2, fontFamily: FONT_BODY, fontSize: 15 }}>
            <Box component="dt" sx={{ color: C.muted }}>
              {l.label}
              {l.note && <Box component="span" sx={{ display: "block", fontSize: 12, fontWeight: 600, color: C.green }}>{l.note}</Box>}
            </Box>
            <Box component="dd" sx={{ m: 0, fontWeight: 600, fontVariantNumeric: "tabular-nums", color: l.free || l.tone === "green" ? C.green : C.ink }}>
              {l.free ? "Free" : money(l.amount)}
            </Box>
          </Box>
        ))}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 2, mt: 0.75, pt: 1.5, borderTop: `1px solid ${C.border}`, fontFamily: FONT_BODY, fontSize: 17, fontWeight: 800, color: C.ink }}>
          <Box component="dt">Total</Box>
          <Box component="dd" sx={{ m: 0, fontVariantNumeric: "tabular-nums" }}>{money(total)}</Box>
        </Box>
      </Box>

      {(parseFloat(o.refundedAmount) > 0 || parseFloat(o.refundedToCard) > 0) && (
        <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 0.75 }}>
          {parseFloat(o.refundedToCard) > 0 && (
            <Box sx={{ display: "flex", justifyContent: "space-between", px: 1.5, py: 1, borderRadius: "12px", bgcolor: C.greenSoft, color: C.green, fontFamily: FONT_BODY, fontSize: 14, fontWeight: 700 }}>
              <span>Refunded to your card</span>
              <span>{money(parseFloat(o.refundedToCard))}</span>
            </Box>
          )}
          {parseFloat(o.refundedAmount) > 0 && (
            <Box sx={{ display: "flex", justifyContent: "space-between", px: 1.5, py: 1, borderRadius: "12px", bgcolor: C.greenSoft, color: C.green, fontFamily: FONT_BODY, fontSize: 14, fontWeight: 700 }}>
              <span>Refunded to your RMF Wallet</span>
              <span>{money(parseFloat(o.refundedAmount))}</span>
            </Box>
          )}
        </Box>
      )}

      <Box sx={{ mt: 1.75, display: "flex", alignItems: "center", gap: 1.25, px: 1.75, py: 1.5, borderRadius: "12px", bgcolor: C.surface, fontFamily: FONT_BODY }}>
        <CreditCardRoundedIcon aria-hidden="true" sx={{ fontSize: 20, color: C.muted }} />
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ fontSize: 14, fontWeight: 600, color: C.ink }}>{paid.text}</Box>
          {paid.wallet > 0 && <Box sx={{ fontSize: 13, color: C.muted }}>{`plus ${money(paid.wallet)} from your RMF Wallet`}</Box>}
        </Box>
      </Box>
    </Box>
  );
}

export default Receipt;
